import { buildProfileView } from "@kovernow/flows/profile";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import { Button } from "@kovernow/react-ui/components/button";
import { DescriptionList } from "@kovernow/react-ui/components/description-list";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Link } from "react-router";
import { Page } from "../components/Page";
import { Recovery } from "../components/Recovery";
import { useServices } from "../core/app-context";
import { useAccountUser, useProfile } from "../core/queries";
import { useT } from "../core/use-t";
import { SignOutButton } from "./Account";

/**
 * W-2 Profile: read-only, fetched fresh on every visit
 * (`GET /usersPrivate/profile`, ID masked by the server). KYC banner, the
 * details list, how to change them, and sign-out. "Verify identity" only
 * while unverified; "Myinfo" wording only in SG.
 */
export function ProfilePage() {
  const t = useT();
  const { config, format } = useServices();
  const profile = useProfile();
  const { account } = useAccountUser();

  let body;
  if (profile.isPending) {
    body = (
      <p aria-busy="true" className="text-muted-foreground">
        {t("common.loading")}
      </p>
    );
  } else if (profile.isError) {
    // No stale data: a failed load shows only the recovery.
    body = (
      <div data-testid="profile-error">
        <Recovery action={toRecoveryAction(profile.error)} onRetry={() => void profile.refetch()} retryable />
      </div>
    );
  } else {
    const view = buildProfileView({ region: config.region, profile: profile.data, account, format });
    body = (
      <div className="flex flex-col gap-6" data-testid="profile">
        <InfoBanner
          data-testid="kyc-banner"
          tone={view.kyc.verified ? "success" : "warning"}
          title={t(view.kyc.bannerTitleKey)}
          body={t(view.kyc.bannerBodyKey)}
          action={
            view.kyc.verifyTo ? (
              <Button asChild variant="outline" size="touch">
                <Link to={view.kyc.verifyTo}>{t("profile.verify")}</Link>
              </Button>
            ) : undefined
          }
        />
        <section aria-labelledby="profile-details">
          <h2 id="profile-details" className="mb-3 text-lg font-semibold">
            {t("profile.detailsTitle")}
          </h2>
          <DescriptionList
            items={view.fields.map((f) => ({
              id: f.id,
              term: t(f.labelKey),
              description: f.value ?? t("profile.notProvided"),
              mono: f.mono && !!f.value,
              muted: !f.value,
            }))}
          />
        </section>
        <p className="text-sm text-muted-foreground">{t(view.kyc.footerKey)}</p>
      </div>
    );
  }

  return (
    <Page titleKey="nav.profile">
      {body}
      <div className="mt-8">
        <SignOutButton />
      </div>
    </Page>
  );
}
